import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userHasGroup, userIsRealAppAdmin } from "@/lib/auth";
import {
  deleteMaintenanceAsset,
  getAddressAssets,
  getHeroLocations,
  listMaintenanceTypes,
  WARTUNGEN_REALTIME_DOMAINS,
  type AddressAsset,
  type HeroLocation,
  type MaintenanceType,
} from "@/lib/wartungen";
import { useRealtimeSync } from "@/lib/useRealtime";

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(parsed);
}

function locationLabel(entry: HeroLocation): string {
  const id = Number(entry.hero_address_id ?? entry.id);
  const label = String(entry.label || entry.name || `Adresse #${id}`).trim();
  return `${label} (#${id})`;
}

export function ObjektUebersichtPage() {
  const { user } = useCurrentUser();
  const canDeleteAsset = userIsRealAppAdmin(user) || userHasGroup(user, "App-Admins");
  const [locations, setLocations] = useState<HeroLocation[]>([]);
  const [types, setTypes] = useState<MaintenanceType[]>([]);
  const [addressId, setAddressId] = useState("");
  const [typeKeys, setTypeKeys] = useState<string[]>([]);
  const [assets, setAssets] = useState<AddressAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingAssetId, setDeletingAssetId] = useState<number | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [locs, ts] = await Promise.all([getHeroLocations(), listMaintenanceTypes()]);
        setLocations(locs);
        setTypes(ts.filter((t) => t.key));
      } catch {
        setLocations([]);
        setTypes([]);
      }
    })();
  }, []);

  const loadAssets = useCallback(async (options?: { silent?: boolean }) => {
    if (!addressId) {
      setAssets([]);
      return;
    }
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const items = await getAddressAssets(addressId, typeKeys);
      setAssets(items);
    } catch (err) {
      setAssets([]);
      setError(err instanceof ApiError ? err.message : "Objekte konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [addressId, typeKeys]);

  useEffect(() => {
    void loadAssets();
  }, [loadAssets]);

  useRealtimeSync({
    domains: [...WARTUNGEN_REALTIME_DOMAINS],
    enabled: !!addressId,
    onChange: () => void loadAssets({ silent: true }),
  });

  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; assets: AddressAsset[] }>();
    for (const asset of assets) {
      const key = String(asset.maintenance_type_key || "—");
      if (!map.has(key)) {
        map.set(key, { label: String(asset.maintenance_type_name || key), assets: [] });
      }
      map.get(key)!.assets.push(asset);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [assets]);

  const stats = useMemo(() => {
    if (!assets.length) return "";
    const linked = assets.filter((a) => Array.isArray(a.links) && a.links.length).length;
    return `${assets.length} Objekt(e), davon ${linked} mit Verknüpfungen.`;
  }, [assets]);

  async function handleDeleteAsset(asset: AddressAsset) {
    const assetId = Number(asset.asset_id ?? asset.id);
    const typeKey = String(asset.maintenance_type_key || asset.type_key || "").trim();
    if (!Number.isFinite(assetId) || assetId <= 0 || !typeKey) {
      setError("Objekt kann nicht gelöscht werden: Typ oder ID fehlt.");
      return;
    }
    const label = String(asset.external_id || asset.name || `#${assetId}`).trim();
    if (
      !window.confirm(
        `Objekt „${label}“ wirklich löschen?\n\nDas Objekt verschwindet aus Web- und iOS-App. Abgeschlossene Wartungshistorie bleibt erhalten.`,
      )
    ) {
      return;
    }
    setDeletingAssetId(assetId);
    setError(null);
    try {
      await deleteMaintenanceAsset(typeKey, assetId);
      setAssets((prev) => prev.filter((entry) => Number(entry.asset_id ?? entry.id) !== assetId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Objekt konnte nicht gelöscht werden.");
    } finally {
      setDeletingAssetId(null);
    }
  }

  return (
    <AppShell
      title="Objekt-Übersicht"
      subtitle="Alle Wartungsobjekte einer Hero-Adresse kategorienübergreifend einsehen."
      back={{ to: "/wartungen", label: "zurück zur Übersicht" }}
    >
      <div className="grid gap-4 rounded-ios border border-separator bg-card p-4 shadow-ios sm:grid-cols-[2fr_2fr_auto] sm:items-end">
        <label className="space-y-1">
          <span className="eh-label text-xs">Hero-Adresse</span>
          <select className="eh-input w-full" value={addressId} onChange={(e) => setAddressId(e.target.value)}>
            <option value="">Bitte wählen …</option>
            {locations.map((loc) => {
              const id = String(loc.hero_address_id ?? loc.id);
              return (
                <option key={id} value={id}>
                  {locationLabel(loc)}
                </option>
              );
            })}
          </select>
        </label>
        <label className="space-y-1">
          <span className="eh-label text-xs">Kategorien (Mehrfachauswahl)</span>
          <select
            multiple
            className="eh-input h-24 w-full"
            value={typeKeys}
            onChange={(e) => setTypeKeys(Array.from(e.target.selectedOptions).map((o) => o.value).filter(Boolean))}
          >
            {types.map((t) => (
              <option key={String(t.key)} value={String(t.key)}>
                {t.name || t.key} ({t.key})
              </option>
            ))}
          </select>
        </label>
        <Button type="button" variant="secondary" onClick={() => void loadAssets()}>
          Aktualisieren
        </Button>
      </div>

      {error ? (
        <Alert variant="error" className="mt-4">
          {error}
        </Alert>
      ) : null}

      <p className="mt-3 text-sm text-label-secondary">{stats}</p>

      <div className="mt-2 space-y-6">
        {!addressId ? (
          <p className="text-sm text-label-secondary">Bitte zuerst eine Hero-Adresse wählen.</p>
        ) : loading ? (
          <p className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner /> Lade Objekte …
          </p>
        ) : assets.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine Objekte für die gewählte Adresse gefunden.</p>
        ) : (
          grouped.map(([key, group]) => (
            <section key={key}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                {group.label} ({group.assets.length})
              </h3>
              <div className="space-y-2">
                {group.assets.map((asset) => (
                  <AssetCard
                    key={String(asset.asset_id ?? asset.id)}
                    asset={asset}
                    canDelete={canDeleteAsset}
                    deleting={deletingAssetId === Number(asset.asset_id ?? asset.id)}
                    onDelete={() => void handleDeleteAsset(asset)}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </AppShell>
  );
}

function AssetCard({
  asset,
  canDelete = false,
  deleting = false,
  onDelete,
}: {
  asset: AddressAsset;
  canDelete?: boolean;
  deleting?: boolean;
  onDelete?: () => void;
}) {
  const links = Array.isArray(asset.links) ? asset.links : [];
  const subLocation = String(asset.sub_location_name || asset.sub_location_id || "").trim();
  const assetId = asset.asset_id ?? asset.id ?? "";
  const typeKey = String(asset.maintenance_type_key || asset.type_key || "").trim();
  return (
    <article className="rounded-lg border border-separator p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <strong className="text-sm text-label">{asset.external_id || `#${assetId}`}</strong>
          {asset.name ? <span className="text-sm text-label-secondary"> – {asset.name}</span> : null}
          {links.length ? (
            <span className="ml-2 rounded-full bg-accent-soft px-2 py-0.5 text-xs text-accent">
              ⛓ {links.length}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {assetId && typeKey ? (
            <Link
              to={`/wartungen/objekt-anlegen?${new URLSearchParams({
                mode: "edit",
                type_key: typeKey,
                asset_id: String(assetId),
                return_to: "objekt_uebersicht",
              }).toString()}`}
              className="inline-flex h-8 items-center rounded-lg bg-card px-3 text-xs font-medium text-accent ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
            >
              Bearbeiten
            </Link>
          ) : null}
          {assetId ? (
            <Link
              to={`/wartungen/huelle-detail?asset_id=${encodeURIComponent(String(assetId))}`}
              className="inline-flex h-8 items-center rounded-lg bg-card px-3 text-xs font-medium text-accent ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
            >
              Detail
            </Link>
          ) : null}
          {canDelete && onDelete ? (
            <Button type="button" variant="danger" size="sm" disabled={deleting} onClick={onDelete}>
              {deleting ? "Lösche …" : "Löschen"}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="mt-1 text-xs text-label-tertiary">
        {subLocation ? `Unterort: ${subLocation} · ` : ""}
        Letzte Wartung: {formatDateTime(asset.last_performed_at)}
      </div>
      {links.length ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-label-secondary">
            Verknüpfte Objekte ({links.length})
          </summary>
          <ul className="ml-3 mt-1 space-y-1 text-xs text-label-secondary">
            {links.map((link, idx) => {
              const other = link.other_asset || {};
              const label = String(other.external_id || other.name || `#${other.asset_id ?? "?"}`).trim();
              const otherType = String(other.maintenance_type_name || other.maintenance_type_key || "").trim();
              return (
                <li key={idx}>
                  ⛓ <strong className="text-label">{label}</strong>
                  {otherType ? <span className="ml-1 text-label-tertiary">({otherType})</span> : null}
                  {other.asset_id ? (
                    <Link
                      to={`/wartungen/huelle-detail?asset_id=${encodeURIComponent(String(other.asset_id))}`}
                      className="ml-2 text-accent hover:underline"
                    >
                      Öffnen
                    </Link>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
    </article>
  );
}
